import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTabVisibility from 'hooks/useTabVisibility'
import { fetchTopAttractions } from 'actions/ExperienceActions'
import { ANYWHERE_PLACE_ID } from 'constants/search'

interface UseTopAttractionsResult {
  attractions: Array<Experiences.Attraction>;
  fetching: boolean;
  isCuratedDestination: boolean | undefined;
}

const BROAD_PLACE_TYPES: ReadonlyArray<App.PlaceType> = ['country', 'continent']

function isBroadPlace(place?: App.Place): boolean {
  if (!place) return false
  if (place.type && BROAD_PLACE_TYPES.includes(place.type)) return true
  // Some country landing places have no type/bounds, so they use the countryCode query path.
  if (!place.type && place.countryCode && !place.boundingRadius) return true
  return false
}

function useTopAttractions(place?: App.Place): UseTopAttractionsResult {
  const dispatch = useAppDispatch()
  const placeId = place?.id
  const placeName = place?.name
  const placeType = place?.type
  const placeCountryCode = place?.countryCode?.toUpperCase()
  const broad = isBroadPlace(place)

  // A place sourced from a static constant can carry only {id, name} until the Places API
  // enrichment lands. Firing with that shape sends a placeType-less request (which the
  // backend treats as unscoped) and the response is cached under the same key, suppressing
  // the corrected request after enrichment. Wait for type/countryCode before fetching so
  // the first request for the key is already the fully-scoped one.
  // Anywhere is a pseudo-place with no type/countryCode and never enriches, so it must
  // count as ready: otherwise its query never dispatches and the carousel spins forever
  // (fetching stuck true, isCuratedDestination never resolves to false).
  const placeReady = !placeId || placeId === ANYWHERE_PLACE_ID || !!placeType || !!placeCountryCode

  const usePlaceQuery = !!placeId && !broad && placeReady

  const key = usePlaceQuery
    ? `top-attractions-place-${placeId}`
    : placeCountryCode
      ? `top-attractions-country-${placeCountryCode}`
      : undefined

  const result = useAppSelector((state) =>
    key ? state.experience.topAttractions[key] : undefined,
  )

  const isTabActive = useTabVisibility()

  const requestTopAttractions = useCallback(() => {
    if (usePlaceQuery) {
      dispatch(
        fetchTopAttractions({
          placeName,
          placeType,
          countryCode: placeCountryCode,
          lePlaceId: placeId,
        }),
      )
    } else if (placeCountryCode) {
      dispatch(fetchTopAttractions({ placeName, placeType, countryCode: placeCountryCode }))
    }
  }, [dispatch, usePlaceQuery, placeId, placeName, placeType, placeCountryCode])

  useEffect(() => {
    if (!key || !isTabActive) return
    requestTopAttractions()
  }, [key, isTabActive, requestTopAttractions])

  const attractions = result?.attractions ?? []
  const topLimit = result?.topAttractionsLimit

  return {
    attractions: topLimit == null ? attractions : attractions.slice(0, topLimit),
    fetching: result?.fetching ?? ((!!key || !placeReady) && !result),
    isCuratedDestination: result?.isCuratedDestination,
  }
}

export default useTopAttractions
